import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { attendanceService, batchService, classNoteService, studentService } from '../services/api';
import { useApp } from '../context/AppContext';
import { formatTiming, isWithinClassHours } from '../utils/time';
import { schoolToday } from '../utils/schoolTime';

// The one fixed reason a whole day can be marked off — kept as a constant
// rather than free text so every marked day means the same thing.
const TEACHER_UNAVAILABLE_NOTE = 'Teacher unavailable';

const todayISO = () => schoolToday();

// 'recording' (watched the class recording instead of attending live)
// counts as attended everywhere a raw 'present' does — it's a separate
// marking, not a separate outcome.
const isAttended = (status) => status === 'present' || status === 'recording';

// Every place a student's name shows up in the attendance views reuses
// this: bold for a paying customer, the phone number on hover (native
// title attribute) since that's the one piece of their
// berlinr-backoffice record actually useful for running a class, and
// their customer id in muted text — the same id used elsewhere (e.g.
// the Students page) to look them up in berlinr-backoffice directly.
const StudentName = ({ student }) => {
  const isPaying = student.customerType === 'paying';
  const title = student.phone || 'No phone on file';
  return (
    <span title={title} className={isPaying ? 'attendance-name-paying' : ''}>
      {student.name} <span className="student-id-tag">#{student.id}</span>
    </span>
  );
};

const statusBadgeClass = (status) => {
  if (status === 'present') return 'badge-active';
  if (status === 'recording') return 'badge-recording';
  if (status === 'absent') return 'badge-danger';
  return 'badge-ended';
};

const statusLabel = (status) => (status === 'recording' ? 'present (recording)' : status);

// Same as statusLabel, but for describing an undo/redo target, where
// the "status" can also be null — meaning fully unmarked/cleared.
const describeStatus = (status) => (status === null ? 'unmarked' : statusLabel(status));

// Pure calendar arithmetic on "YYYY-MM"/"YYYY-MM-DD" strings — not tied to
// "now", so plain local-time Date math is fine here (see
// utils/schoolTime.js for why "now" itself needs the school's timezone).
const shiftMonth = (yearMonth, months) => {
  const [y, m] = yearMonth.split('-').map(Number);
  const dt = new Date(y, m - 1 + months, 1);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
};

const formatMonthLabel = (yearMonth) => {
  const [y, m] = yearMonth.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
};

// Every "YYYY-MM" from start through end, inclusive.
const monthsBetween = (startYearMonth, endYearMonth) => {
  const months = [];
  let cursor = startYearMonth;
  while (cursor <= endYearMonth) {
    months.push(cursor);
    cursor = shiftMonth(cursor, 1);
  }
  return months;
};

// Every weekday (Mon–Fri) in the given month, as ISO dates — weekends are
// skipped since there's no class to take attendance for on one.
const weekdaysInMonth = (yearMonth) => {
  const [y, m] = yearMonth.split('-').map(Number);
  const daysInMonth = new Date(y, m, 0).getDate();
  const dates = [];
  for (let d = 1; d <= daysInMonth; d++) {
    const dt = new Date(y, m - 1, d);
    const dow = dt.getDay();
    if (dow !== 0 && dow !== 6) {
      dates.push(`${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
    }
  }
  return dates;
};

const formatShortDate = (iso) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const isWeekday = (iso) => {
  const [y, m, d] = iso.split('-').map(Number);
  const dow = new Date(y, m - 1, d).getDay();
  return dow !== 0 && dow !== 6;
};

// Monday — the first weekday column of each week — gets a divider so
// consecutive weeks are visually separated despite weekends being skipped.
const isWeekStart = (iso) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).getDay() === 1;
};

// Full calendar grid for a month, Monday-first (matching isWeekStart's
// convention), flattened to one array of 7*N cells — null pads out days
// outside the month so every week is a complete row. Unlike
// weekdaysInMonth (which skips weekends entirely, fine for a column-per-
// day marking grid), an actual calendar view needs the real grid shape,
// blanks included.
const calendarGrid = (yearMonth) => {
  const [y, m] = yearMonth.split('-').map(Number);
  const daysInMonth = new Date(y, m, 0).getDate();
  const firstDow = new Date(y, m - 1, 1).getDay(); // 0=Sun..6=Sat
  const leadingBlanks = (firstDow + 6) % 7; // days before Monday
  const dates = [];
  for (let d = 1; d <= daysInMonth; d++) {
    dates.push(`${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
  }
  const cells = [...Array(leadingBlanks).fill(null), ...dates];
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
};

// A day can (rarely) carry more than one record — different batches on
// the same date. For tinting the whole cell, absent outranks recording
// outranks present, so a day with any miss is never masked by an
// unrelated batch's present mark.
const CALENDAR_STATUS_PRIORITY = { absent: 0, recording: 1, present: 2 };
const dominantCalendarStatus = (dayRecords) =>
  dayRecords.reduce(
    (worst, r) => (CALENDAR_STATUS_PRIORITY[r.status] < CALENDAR_STATUS_PRIORITY[worst] ? r.status : worst),
    'present'
  );

const CALENDAR_STATUS_LETTER = { present: 'P', recording: 'R', absent: 'A' };

const TeacherAttendance = () => {
  const { notify } = useApp();
  const [searchParams] = useSearchParams();
  const linkedBatchId = searchParams.get('batchId');
  const [batches, setBatches] = useState([]);
  const [selectedBatchId, setSelectedBatchId] = useState('');
  const [roster, setRoster] = useState([]);
  const [records, setRecords] = useState([]);
  const [lastAction, setLastAction] = useState(null);
  const [noteEditingId, setNoteEditingId] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [monthAnchor, setMonthAnchor] = useState(todayISO().slice(0, 7));
  const [sortBy, setSortBy] = useState('attendance');
  const [viewMode, setViewMode] = useState('all'); // 'month' | 'all'
  // Plain component state, never persisted — a fresh page load always
  // starts with every column visible again, as requested.
  const [hiddenDates, setHiddenDates] = useState(() => new Set());
  const [unavailableDates, setUnavailableDates] = useState([]);

  useEffect(() => {
    batchService
      .list()
      .then((allBatches) => {
        // External batches are for the record only — no attendance is ever
        // taken for one, so they don't belong in this picker at all.
        const data = allBatches.filter((b) => !b.isExternal);
        setBatches(data);
        // Deep-link from a specific batch (e.g. Batches page's "View
        // Attendance") wins; otherwise, only one batch to choose from
        // means there's nothing to pick — just select it.
        if (linkedBatchId && data.some((b) => String(b.id) === linkedBatchId)) {
          setSelectedBatchId(linkedBatchId);
        } else if (data.length === 1) {
          setSelectedBatchId(String(data[0].id));
        }
      })
      .catch((err) => notify(err.message, 'error'));
  }, [notify, linkedBatchId]);

  useEffect(() => {
    if (!selectedBatchId) return;
    batchService.roster(selectedBatchId).then(setRoster).catch((err) => notify(err.message, 'error'));
  }, [selectedBatchId, notify]);

  useEffect(() => {
    // Land on the batch's own start month rather than an empty "today's
    // month" grid when that batch hadn't started yet this month.
    const batch = batches.find((b) => String(b.id) === selectedBatchId);
    const startMonth = batch?.startDate?.slice(0, 7);
    if (startMonth) setMonthAnchor((prev) => (prev < startMonth ? startMonth : prev));
  }, [selectedBatchId, batches]);

  const loadRecords = useCallback(() => {
    if (!selectedBatchId) return;
    attendanceService.forBatch(selectedBatchId).then(setRecords).catch((err) => notify(err.message, 'error'));
  }, [selectedBatchId, notify]);

  useEffect(() => {
    loadRecords();
    setLastAction(null);
  }, [loadRecords]);

  const loadUnavailableDates = useCallback(() => {
    if (!selectedBatchId) return;
    classNoteService.listForBatch(selectedBatchId)
      .then((notes) => setUnavailableDates(notes.map((n) => n.date)))
      .catch((err) => notify(err.message, 'error'));
  }, [selectedBatchId, notify]);

  useEffect(() => {
    loadUnavailableDates();
  }, [loadUnavailableDates]);

  const unavailableSet = new Set(unavailableDates);

  const handleToggleUnavailable = async (date) => {
    const wasUnavailable = unavailableSet.has(date);
    try {
      await classNoteService.setNote(selectedBatchId, date, wasUnavailable ? '' : TEACHER_UNAVAILABLE_NOTE);
      setUnavailableDates((prev) => (wasUnavailable ? prev.filter((d) => d !== date) : [...prev, date]));
    } catch (err) {
      notify(err.message, 'error');
    }
  };

  const selectedBatch = batches.find((b) => String(b.id) === selectedBatchId);
  const currentMonthAnchor = todayISO().slice(0, 7);
  const startMonthAnchor = selectedBatch?.startDate?.slice(0, 7);

  // Days before the batch even started have nothing to show — skip them
  // rather than padding the grid with columns that can never be marked.
  // "All" mode spans every month from the batch's start through today in
  // one continuous, horizontally-scrollable grid instead of one month
  // at a time.
  const dateColumns = viewMode === 'all' && startMonthAnchor
    ? monthsBetween(startMonthAnchor, currentMonthAnchor)
        .flatMap(weekdaysInMonth)
        .filter((d) => d >= selectedBatch.startDate && d <= todayISO())
    : weekdaysInMonth(monthAnchor).filter((d) => !selectedBatch?.startDate || d >= selectedBatch.startDate);

  const visibleDateColumns = dateColumns.filter((d) => !hiddenDates.has(d));

  // Weekdays that have actually happened and weren't marked teacher-
  // unavailable — the real count of classes held, not just calendar
  // weekdays in the current view. This is the fixed denominator for
  // every student's ratio below — a day nobody bothered to check counts
  // as not-present, same as an explicit "absent", rather than silently
  // shrinking that student's denominator. Today is excluded even though
  // it's already markable — its class hasn't finished yet, so it isn't
  // "held" for this purpose until tomorrow.
  const heldClassDays = dateColumns.filter((d) => d < todayISO() && !unavailableSet.has(d));
  const classDayCount = heldClassDays.length;

  const hideColumn = (date) => setHiddenDates((prev) => new Set(prev).add(date));
  const unhideAll = () => setHiddenDates(new Set());

  const statusMap = {};
  records.forEach((r) => { statusMap[`${r.studentId}|${r.date}`] = r.status; });

  // Per-day headcount ("10/21") for the footer row — out of the whole
  // roster, not just students with a record, same denominator logic as
  // each student's own ratio above.
  const presentCountByDate = {};
  visibleDateColumns.forEach((date) => {
    presentCountByDate[date] = roster.filter((s) => isAttended(statusMap[`${s.id}|${date}`])).length;
  });

  // Shared by the checkbox, the recording toggle, and the explicit
  // "absent" button — all three just write one of the three statuses.
  // The one place that actually writes a cell's status — target is
  // 'present' | 'absent' | 'recording' | null (null clears the record
  // entirely, back to fully unmarked). Used directly by the checkbox/
  // R/A controls, and by handleUndo/handleRedo replaying the reverse
  // or forward side of whatever the last one of those did — so a mark,
  // a toggle-off, and an undo/redo of either all go through the exact
  // same read-current-state/write/optimistic-update path.
  const applyStatus = async (studentId, date, targetStatus) => {
    // Belt-and-suspenders — every control that calls this is already
    // disabled for future dates, but don't rely on that alone; the
    // backend rejects this too.
    if (date > todayISO()) return null;
    const key = `${studentId}|${date}`;
    const existing = records.find((r) => r.studentId === studentId && r.date === date) || null;
    const beforeStatus = existing ? existing.status : null;
    if (targetStatus === beforeStatus) return null;
    try {
      if (targetStatus === null) {
        if (existing) await attendanceService.unmark(existing.id);
        setRecords((prev) => prev.filter((r) => `${r.studentId}|${r.date}` !== key));
      } else {
        setRecords((prev) => [...prev.filter((r) => `${r.studentId}|${r.date}` !== key), { studentId, date, status: targetStatus }]);
        const record = await attendanceService.mark(selectedBatchId, studentId, date, targetStatus);
        setRecords((prev) => [...prev.filter((r) => `${r.studentId}|${r.date}` !== key), record]);
      }
      return { studentId, date, beforeStatus, afterStatus: targetStatus };
    } catch (err) {
      notify(err.message, 'error');
      loadRecords();
      return null;
    }
  };

  const markStatus = async (studentId, date, status) => {
    const action = await applyStatus(studentId, date, status);
    if (!action) return;
    setLastAction({
      ...action,
      studentName: roster.find((s) => s.id === studentId)?.name || '',
      undone: false,
    });
  };

  const handleUndo = async () => {
    if (!lastAction || lastAction.undone) return;
    const action = await applyStatus(lastAction.studentId, lastAction.date, lastAction.beforeStatus);
    if (action) setLastAction((prev) => (prev ? { ...prev, undone: true } : prev));
  };

  const handleRedo = async () => {
    if (!lastAction || !lastAction.undone) return;
    const action = await applyStatus(lastAction.studentId, lastAction.date, lastAction.afterStatus);
    if (action) setLastAction((prev) => (prev ? { ...prev, undone: false } : prev));
  };

  const handleToggle = (studentId, date, checked) => markStatus(studentId, date, checked ? 'present' : 'absent');

  // Same toggle-off-clears-to-null shape as handleSetAbsent below — the
  // present button used to be a plain checkbox (see handleToggle, still
  // used by the mobile simplified list), which unchecked into 'absent'
  // rather than back to unmarked. Made this one consistent with A/R
  // instead of matching the checkbox's old behavior.
  const handleSetPresent = (studentId, date) =>
    markStatus(studentId, date, statusMap[`${studentId}|${date}`] === 'present' ? null : 'present');

  // A separate marking from the checkbox above — for a student who
  // watched the class recording instead of attending live. Toggles
  // 'recording' on/off directly rather than reading a checkbox's
  // checked state, since it's not driven by one.
  const handleToggleRecording = (studentId, date) =>
    markStatus(studentId, date, statusMap[`${studentId}|${date}`] === 'recording' ? 'absent' : 'recording');

  // Explicit "mark absent" — a direct entry point distinct from
  // unchecking the other two, so a day can be recorded absent without
  // first checking then un-checking present or recording. Clicking it
  // again clears the day entirely instead of re-writing the same
  // status — re-marking an unchanged status used to still count as a
  // "new" action and steal the Undo target from whatever came before it.
  const handleSetAbsent = (studentId, date) =>
    markStatus(studentId, date, statusMap[`${studentId}|${date}`] === 'absent' ? null : 'absent');

  const startNoteEdit = (s) => {
    setNoteEditingId(s.id);
    setNoteText(s.note?.comment || '');
  };

  const cancelNoteEdit = () => setNoteEditingId(null);

  const handleSaveNote = async (e, studentId) => {
    e.preventDefault();
    try {
      const note = await studentService.saveNote(studentId, noteText);
      setRoster((prev) => prev.map((s) => (s.id === studentId ? { ...s, note } : s)));
      setNoteEditingId(null);
    } catch (err) {
      notify(err.message, 'error');
    }
  };

  // pct is rounded for display; rawPct keeps full precision so students
  // whose rounded percentages tie (very common with small totals, e.g.
  // 4/5 vs 5/6 both rounding to 80%) still sort in their true order
  // instead of falling back to whatever order they happened to be in.
  // total is always classDayCount — everyone's denominator matches the
  // "N class days" figure shown above the grid, not however many of
  // those days happen to have a record.
  const overviewByStudent = roster.map((s) => {
    const presentCount = heldClassDays.filter((d) => isAttended(statusMap[`${s.id}|${d}`])).length;
    const total = classDayCount;
    const absentCount = total - presentCount;
    const rawPct = total > 0 ? presentCount / total : null;
    const pct = rawPct === null ? null : Math.round(rawPct * 100);
    return { student: s, presentCount, absentCount, total, pct, rawPct };
  });

  // Shared ordering for both the grid and the overview table below, so
  // they never disagree about which row is which. Ranked over the same
  // class days as classDayCount — switch to "All days" to rank over the
  // batch's whole history instead of just the viewed month.
  const sortedOverview = [...overviewByStudent].sort((a, b) => {
    if (sortBy === 'attendance') {
      // Nobody-marked-yet sorts last regardless of direction — there's no
      // attendance rate to rank it by.
      if (a.rawPct === null && b.rawPct === null) return a.student.name.localeCompare(b.student.name);
      if (a.rawPct === null) return 1;
      if (b.rawPct === null) return -1;
      if (b.rawPct !== a.rawPct) return b.rawPct - a.rawPct;
      return a.student.name.localeCompare(b.student.name);
    }
    return a.student.name.localeCompare(b.student.name);
  });

  // Mobile-only alternate view (see responsive.css) — the full date grid
  // is unusable on a phone (many narrow columns, tiny checkboxes), so on
  // small screens a simplified one-checkbox-per-name list for just today
  // renders instead. Same data/handlers as the grid, just today's slice
  // of it; the desktop grid markup below is completely unaffected.
  const today = todayISO();
  const todayIsInRange = (!selectedBatch?.startDate || today >= selectedBatch.startDate) && selectedBatch?.status !== 'ended';
  const showTodayList = isWeekday(today) && todayIsInRange;
  const todayIsUnavailable = unavailableSet.has(today);

  return (
    <div className="page">
      <h1>Attendance</h1>
      <div className="card">
        <div className="inline-form">
          <select className="batch-select" value={selectedBatchId} onChange={(e) => setSelectedBatchId(e.target.value)}>
            <option value="">Select a batch…</option>
            {batches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}{formatTiming(b) ? ` (${formatTiming(b)})` : ''}
              </option>
            ))}
          </select>
          {selectedBatchId && (
            <>
              {viewMode === 'month' ? (
                <>
                  {(!startMonthAnchor || monthAnchor > startMonthAnchor) && (
                    <button type="button" className="btn-secondary" onClick={() => setMonthAnchor((prev) => shiftMonth(prev, -1))}>
                      ← Previous month
                    </button>
                  )}
                  <span className="batch-meta">{formatMonthLabel(monthAnchor)}</span>
                  {monthAnchor < currentMonthAnchor && (
                    <button type="button" className="btn-secondary" onClick={() => setMonthAnchor((prev) => shiftMonth(prev, 1))}>
                      Next month →
                    </button>
                  )}
                </>
              ) : (
                <span className="batch-meta">All days{startMonthAnchor ? ` (from ${formatMonthLabel(startMonthAnchor)})` : ''}</span>
              )}
              <button type="button" className="btn-secondary" onClick={() => setViewMode((m) => (m === 'all' ? 'month' : 'all'))}>
                {viewMode === 'all' ? 'Show by month' : 'Show all days'}
              </button>
              {hiddenDates.size > 0 && (
                <button type="button" className="btn-secondary" onClick={unhideAll}>
                  Show {hiddenDates.size} hidden column{hiddenDates.size === 1 ? '' : 's'}
                </button>
              )}
              <span className="batch-meta">{classDayCount} class day{classDayCount === 1 ? '' : 's'}</span>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="name">Sort: Name (A–Z)</option>
                <option value="attendance">Sort: Attendance (high–low)</option>
              </select>
              <button
                type="button"
                className="btn-secondary"
                disabled={!lastAction || lastAction.undone}
                onClick={handleUndo}
                title={lastAction && !lastAction.undone ? `Undo: ${lastAction.studentName} → ${describeStatus(lastAction.afterStatus)} for ${formatShortDate(lastAction.date)}` : 'Nothing to undo'}
              >
                Undo
              </button>
              <button
                type="button"
                className="btn-secondary"
                disabled={!lastAction || !lastAction.undone}
                onClick={handleRedo}
                title={lastAction && lastAction.undone ? `Redo: ${lastAction.studentName} → ${describeStatus(lastAction.afterStatus)} for ${formatShortDate(lastAction.date)}` : 'Nothing to redo'}
              >
                Redo
              </button>
              <span className="batch-meta">P = attended live · R = watched the recording · A = marked absent</span>
            </>
          )}
        </div>

        {selectedBatchId && (
          <>
            <div className="table-wrap attendance-grid-wrap">
              <table className="table attendance-grid">
                <thead>
                  <tr>
                    <th>Student ({roster.length})</th>
                    {visibleDateColumns.map((date) => (
                      <th
                        key={date}
                        className={[
                          isWeekStart(date) ? 'attendance-week-start' : '',
                          unavailableSet.has(date) ? 'attendance-cell-unavailable' : '',
                        ].filter(Boolean).join(' ')}
                      >
                        <button
                          type="button"
                          className="attendance-date-btn"
                          onClick={() => handleToggleUnavailable(date)}
                          title={unavailableSet.has(date) ? 'Teacher unavailable — click to unmark' : 'Mark teacher unavailable for this day'}
                        >
                          {formatShortDate(date)}
                        </button>
                        <button
                          type="button"
                          className="attendance-hide-btn"
                          onClick={() => hideColumn(date)}
                          title="Hide this column"
                          aria-label={`Hide ${formatShortDate(date)}`}
                        >
                          ×
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedOverview.map(({ student: s, presentCount, total, pct }) => (
                    <tr key={s.id}>
                      <td>
                        {noteEditingId === s.id ? (
                          <form className="student-note-form" onSubmit={(e) => handleSaveNote(e, s.id)}>
                            <textarea
                              value={noteText}
                              onChange={(e) => setNoteText(e.target.value)}
                              placeholder="Reason for missed classes, etc."
                              rows={2}
                              autoFocus
                            />
                            <div className="btn-group">
                              <button type="submit" className="btn-link">Save</button>
                              <button type="button" className="btn-link" onClick={cancelNoteEdit}>Cancel</button>
                            </div>
                          </form>
                        ) : (
                          <div className="attendance-name-cell">
                            <StudentName student={s} />
                            {total > 0 && <span className="attendance-stat">{presentCount}/{total} - {pct}%</span>}
                            <button
                              type="button"
                              className={`attendance-note-btn${s.note ? ' has-note' : ''}`}
                              onClick={() => startNoteEdit(s)}
                              title={s.note ? s.note.comment : 'Add a note'}
                            >
                              {s.note ? 'Edit note' : '+ Note'}
                            </button>
                          </div>
                        )}
                      </td>
                      {visibleDateColumns.map((date) => {
                        const status = statusMap[`${s.id}|${date}`];
                        const isFuture = date > todayISO();
                        const isUnavailable = unavailableSet.has(date);
                        return (
                          <td
                            key={date}
                            className={[
                              status === 'present' ? 'attendance-cell-present' : '',
                              status === 'recording' ? 'attendance-cell-recording' : '',
                              isWeekStart(date) ? 'attendance-week-start' : '',
                              isUnavailable ? 'attendance-cell-unavailable' : '',
                            ].filter(Boolean).join(' ')}
                          >
                            <div className="attendance-cell-controls">
                              {status !== 'present' && status !== 'recording' && (
                                <button
                                  type="button"
                                  className={`attendance-absent-btn${status === 'absent' ? ' is-active' : ''}`}
                                  disabled={isFuture || isUnavailable}
                                  onClick={() => handleSetAbsent(s.id, date)}
                                  title={status === 'absent' ? 'Marked absent — click to unmark' : 'Mark absent'}
                                >
                                  A
                                </button>
                              )}
                              {status !== 'recording' && status !== 'absent' && (
                                <button
                                  type="button"
                                  className={`attendance-present-btn${status === 'present' ? ' is-active' : ''}`}
                                  disabled={isFuture || isUnavailable}
                                  onClick={() => handleSetPresent(s.id, date)}
                                  title={status === 'present' ? 'Marked present — click to unmark' : 'Mark present'}
                                >
                                  P
                                </button>
                              )}
                              {status !== 'present' && status !== 'absent' && (
                                <button
                                  type="button"
                                  className={`attendance-recording-btn${status === 'recording' ? ' is-active' : ''}`}
                                  disabled={isFuture || isUnavailable}
                                  onClick={() => handleToggleRecording(s.id, date)}
                                  title={status === 'recording' ? 'Attended via recording — click to unmark' : 'Mark as attended via recording'}
                                >
                                  R
                                </button>
                              )}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  {roster.length === 0 && (
                    <tr>
                      <td colSpan={visibleDateColumns.length + 1}>No students enrolled in this batch.</td>
                    </tr>
                  )}
                </tbody>
                {roster.length > 0 && (
                  <tfoot>
                    <tr className="attendance-summary-row">
                      <td>Present</td>
                      {visibleDateColumns.map((date) => {
                        const isFuture = date > todayISO();
                        const isUnavailable = unavailableSet.has(date);
                        return (
                          <td
                            key={date}
                            className={[
                              isWeekStart(date) ? 'attendance-week-start' : '',
                              isUnavailable ? 'attendance-cell-unavailable' : '',
                            ].filter(Boolean).join(' ')}
                          >
                            {isFuture ? '—' : `${presentCountByDate[date]}/${roster.length}`}
                          </td>
                        );
                      })}
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>

            <div className="attendance-mobile-today">
              {showTodayList ? (
                <>
                  <p className="stat-note">{formatShortDate(today)} — tap a name to mark present.</p>
                  {todayIsUnavailable && <p className="stat-note">Teacher unavailable today — marking is off.</p>}
                  <ul className="attendance-today-list">
                    {sortedOverview.map(({ student: s }) => {
                      const status = statusMap[`${s.id}|${today}`];
                      return (
                        <li key={s.id}>
                          <StudentName student={s} />
                          <input
                            type="checkbox"
                            checked={status === 'present'}
                            disabled={todayIsUnavailable}
                            onChange={(e) => handleToggle(s.id, today, e.target.checked)}
                          />
                        </li>
                      );
                    })}
                    {roster.length === 0 && <li>No students enrolled in this batch.</li>}
                  </ul>
                </>
              ) : (
                <p className="stat-note">No class today for this batch.</p>
              )}
            </div>
          </>
        )}
      </div>

      {selectedBatchId && (
        <div className="card">
          <h2>Attendance Overview</h2>
          {records.length === 0 ? (
            <p>No attendance marked yet for this batch.</p>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Present</th>
                    <th>Absent</th>
                    <th>Days Marked</th>
                    <th>Attendance %</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedOverview.map(({ student, presentCount, absentCount, total, pct }) => (
                    <tr key={student.id}>
                      <td><StudentName student={student} /></td>
                      <td>{presentCount}</td>
                      <td>{absentCount}</td>
                      <td>{total}</td>
                      <td>{pct === null ? '—' : `${pct}%`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const CALENDAR_WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const StudentAttendance = () => {
  const { notify } = useApp();
  const [batches, setBatches] = useState([]);
  const [records, setRecords] = useState([]);
  const [unavailableDates, setUnavailableDates] = useState([]);
  const [checkingInId, setCheckingInId] = useState(null);
  const [monthAnchor, setMonthAnchor] = useState(todayISO().slice(0, 7));

  const loadAll = useCallback(() => {
    batchService.mine().then(setBatches).catch((err) => notify(err.message, 'error'));
    attendanceService.mine().then(setRecords).catch((err) => notify(err.message, 'error'));
    classNoteService.mine().then((notes) => setUnavailableDates(notes.map((n) => n.date))).catch((err) => notify(err.message, 'error'));
  }, [notify]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const today = todayISO();
  const activeBatches = batches.filter((b) => b.status !== 'ended' && !b.isExternal);
  const todaysRecordFor = (batchId) => records.find((r) => r.batch.id === batchId && r.date === today);

  const recordsByDate = records.reduce((acc, r) => {
    (acc[r.date] = acc[r.date] || []).push(r);
    return acc;
  }, {});
  const unavailableSet = new Set(unavailableDates);

  const handleCheckIn = async (batchId) => {
    setCheckingInId(batchId);
    try {
      const res = await attendanceService.checkin(batchId);
      notify(res.message, res.checkedIn ? 'success' : 'info');
      loadAll();
    } catch (err) {
      notify(err.message, 'error');
    } finally {
      setCheckingInId(null);
    }
  };

  return (
    <div className="page">
      <h1>My Attendance</h1>

      {activeBatches.length > 0 && (
        <div className="card">
          <h2>Today's Sessions</h2>
          <ul className="list">
            {activeBatches.map((b) => {
              const todaysRecord = todaysRecordFor(b.id);
              const withinHours = isWithinClassHours(b);
              return (
                <li key={b.id} className="attendance-row">
                  <span>
                    {b.name}{b.subject && ` — ${b.subject}`}
                    {formatTiming(b) && <span className="batch-meta"> · {formatTiming(b)}</span>}
                  </span>
                  {todaysRecord ? (
                    <span className={`badge ${statusBadgeClass(todaysRecord.status)}`}>Marked {statusLabel(todaysRecord.status)}</span>
                  ) : withinHours ? (
                    <button
                      className="btn-primary"
                      disabled={checkingInId === b.id}
                      onClick={() => handleCheckIn(b.id)}
                    >
                      {checkingInId === b.id ? 'Checking in…' : 'Check In'}
                    </button>
                  ) : (
                    <span className="batch-meta">Available during class hours</span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="card">
        <h2>Calendar</h2>
        <div className="inline-form">
          <button type="button" className="btn-secondary" onClick={() => setMonthAnchor((prev) => shiftMonth(prev, -1))}>
            ← Previous month
          </button>
          <span className="batch-meta">{formatMonthLabel(monthAnchor)}</span>
          {monthAnchor < today.slice(0, 7) && (
            <button type="button" className="btn-secondary" onClick={() => setMonthAnchor((prev) => shiftMonth(prev, 1))}>
              Next month →
            </button>
          )}
        </div>
        <div className="calendar-grid">
          {CALENDAR_WEEKDAY_LABELS.map((label, i) => (
            <div key={label} className={`calendar-weekday${i >= 5 ? ' calendar-weekday-weekend' : ''}`}>{label}</div>
          ))}
          {calendarGrid(monthAnchor).map((iso, i) => {
            if (!iso) return <div key={`blank-${i}`} className="calendar-cell calendar-cell-empty" />;
            const dayRecords = recordsByDate[iso] || [];
            const cellStatus = dayRecords.length ? dominantCalendarStatus(dayRecords) : null;
            // A day with no attendance record can still mean something —
            // the teacher marked it unavailable rather than it just being
            // data nobody's entered yet. Only shown when there's no actual
            // attendance record already explaining the day.
            const isUnavailable = !cellStatus && unavailableSet.has(iso);
            const weekendClass = !cellStatus && !isUnavailable && !isWeekday(iso) ? ' calendar-cell-weekend' : '';
            return (
              <div
                key={iso}
                className={`calendar-cell${cellStatus ? ` calendar-cell-${cellStatus}` : ''}${isUnavailable ? ' calendar-cell-unavailable' : ''}${weekendClass}${iso === today ? ' calendar-cell-today' : ''}`}
              >
                <span className="calendar-date-num">{Number(iso.slice(-2))}</span>
                {dayRecords.length > 0 && (
                  <div className="calendar-day-marks">
                    {dayRecords.map((r) => (
                      <span
                        key={r.id}
                        className={`calendar-mark calendar-mark-${r.status}`}
                        title={`${r.batch.name} — ${statusLabel(r.status)}`}
                      >
                        {CALENDAR_STATUS_LETTER[r.status]}
                      </span>
                    ))}
                  </div>
                )}
                {isUnavailable && (
                  <div className="calendar-day-marks">
                    <span className="calendar-mark calendar-mark-unavailable" title="Teacher unavailable">U</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="calendar-legend">
          <span><span className="calendar-mark calendar-mark-present">P</span> Present</span>
          <span><span className="calendar-mark calendar-mark-recording">R</span> Recording</span>
          <span><span className="calendar-mark calendar-mark-absent">A</span> Absent</span>
          <span><span className="calendar-mark calendar-mark-unavailable">U</span> Teacher unavailable</span>
        </div>
      </div>
    </div>
  );
};

const Attendance = () => {
  const { currentUser } = useApp();
  return currentUser.role === 'student' ? <StudentAttendance /> : <TeacherAttendance />;
};

export default Attendance;
